import { memo, type HTMLAttributes } from 'react';

export interface FogOverlayProps extends HTMLAttributes<HTMLDivElement> {
  /** Whether the underlying square is light (for contrast) */
  isLight: boolean;
  /** Opacity level (0-1) - default 0.85 for strong fog */
  opacity?: number;
}

/**
 * FogOverlay — Memoized Fog of War overlay.
 *
 * Renders a layered visual mask over squares containing opponent pieces
 * (hidden: true). Preserves board grid clarity while concealing enemy piece
 * identity.
 *
 * ACCESSIBILITY — Mandate A (Colour-blind safety):
 * A fogged square must NOT be identifiable by colour alone, otherwise the game
 * is unplayable for colour-blind users (a grey square vs a board square is a
 * pure hue/lightness cue). This overlay therefore layers a distinct
 * DIAGONAL-STRIPE pattern (repeating-linear-gradient, ~45°, ~6px pitch) using
 * the theme `--fog-dark` token over the frosted base. The geometric contour is
 * universally legible regardless of hue perception, while the frosted base +
 * organic noise still obscure the hidden piece silhouette beneath (no identity
 * leak). All layers are CSS-only — zero JS, zero animation, zero re-render cost
 * (custom React.memo comparator unchanged) — preserving the 60FPS mandate.
 *
 * Layering (DOM order, all `position:absolute;inset:0;pointerEvents:none`):
 *   1. frosted base (linear-gradient + backdrop-filter)
 *   2. diagonal-stripe contour signal
 *   3. organic noise texture ("dust cloud")
 */
export const FogOverlay = memo(function FogOverlay({
  isLight,
  opacity = 0.88,
  className,
  style,
  children,
  ...rest
}: FogOverlayProps) {
  return (
    <div
      className={className}
      style={{
        ...style,
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: isLight
          ? `linear-gradient(135deg, hsl(var(--muted) / ${opacity * 0.9}) 0%, hsl(var(--muted) / ${opacity}) 50%, hsl(var(--muted) / ${opacity * 1.1}) 100%)`
          : `linear-gradient(135deg, hsl(var(--muted) / ${opacity}) 0%, hsl(var(--muted) / ${opacity * 1.1}) 50%, hsl(var(--muted) / ${opacity}) 100%)`,
        backdropFilter: 'blur(1.5px)',
        WebkitBackdropFilter: 'blur(1.5px)',
        borderRadius: 'inherit',
        zIndex: 10,
      }}
      {...rest}
    >
      {/* Colour-blind-safe contour: diagonal stripes over the frosted base.
          Pitch (~6px) chosen so the pattern reads clearly without fully
          erasing the base gradient's depth cue. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `repeating-linear-gradient(45deg, hsl(var(--fog-dark) / 0.45) 0 3px, transparent 3px 9px)`,
          pointerEvents: 'none',
        }}
      />

      {/* Subtle noise pattern for "dust cloud" texture */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E")`,
          opacity: 0.6,
          pointerEvents: 'none',
        }}
      />
      {children}
    </div>
  );
}, (prev, next) => {
  // Custom memoization: only re-render if isLight or opacity changes.
  return prev.isLight === next.isLight && prev.opacity === next.opacity;
});

FogOverlay.displayName = 'FogOverlay';
